import { useTheme } from '@mui/material/styles';
import type { AlternativesShops, GeoCountryCode } from '@packages/config/src/default';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { Link } from '@packages/modules/src/Link/Link';
import { Box } from '@packages/shared/src/components/Box/Box';
import { Image } from '@packages/shared/src/components/Image/Image';
import { Stack } from '@packages/shared/src/components/Stack/Stack';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getWithExpiry, setWithExpiry } from '../../utils/localStorage';
import { GeoCountryAlternatives } from './GeoCountryLayer.alternatives';
import { GeoCountryFlag } from './GeoCountryLayer.flag';
import { messages } from './GeoCountryLayer.messages';

const day = 1000 * 60 * 60 * 24;
const expiryTimeToLive = 30 * day; // 30 days

const expiryKey = 'geoCountryLayer';

export const dialogTestId = 'gcl-dialog';

const getAlternatives = (
  alternatives?: AlternativesShops[],
  geoCountryCode?: GeoCountryCode,
): AlternativesShops[] | undefined => {
  const filtered = alternatives?.filter((a) => a.flag === geoCountryCode);
  return (filtered?.length ?? 0) > 0 ? filtered : alternatives;
};

/**
 * GeoCountryLayer component will be displayed for users that come from a country that is not
 * served by this shop. This component is purely clientside rendered - it depends on localStorage
 * */
export const GeoCountryLayer = ({ geoCountryCode }: { geoCountryCode: GeoCountryCode }) => {
  const {
    host: { domain },
    tenantId,
    geoCountryLayer,
    order: { paths },
  } = useConfig();
  const pathname = usePathname();
  const { locale } = useI18n();
  const { spacing } = useTheme();
  const { isMobile } = useDeviceType();
  const { formatMessage } = useIntl();
  const fullDomain = `www.${domain}`;

  const imageSrc = geoCountryLayer?.img?.path.replace('<tenant>', tenantId);
  const expiryVal = getWithExpiry(expiryKey);

  const [showGeoCountryLayer, setShowGeoCountryLayer] = useState(
    geoCountryLayer.enabled && !expiryVal && geoCountryCode?.toLowerCase() !== geoCountryLayer.flag,
  );

  const handleClose = () => {
    setWithExpiry(expiryKey, 'GeoLayerHasSeen', expiryTimeToLive);
    setShowGeoCountryLayer(false);
  };

  /* ORDER-2815 no country layer for checkout pages */
  const isCheckoutPath = Object.values(paths).find(
    (localizedPath) => localizedPath[locale] === pathname || localizedPath.default === pathname,
  );

  if (isCheckoutPath || !showGeoCountryLayer) {
    return null;
  }

  return (
    <Dialog isOpen={showGeoCountryLayer} onClose={handleClose}>
      <DialogHeader
        onClose={handleClose}
      >{`${formatMessage(messages.headline)} ${fullDomain}`}</DialogHeader>
      <DialogContent>
        <Box
          sx={{
            overflowY: 'auto', // scroll only the content and not the whole dialog
            padding: 3,
          }}
        >
          <span className="icon-emp-logo mb-1 h-4 w-[6rem] md:h-6 md:w-[10rem]" aria-hidden />
          <Notification severity="warning">{formatMessage(messages.info)}</Notification>
          <Stack direction="row" justifyContent="left" spacing={5} sx={{ paddingTop: 2 }}>
            {!isMobile && (
              <Image
                src={imageSrc}
                alt="freundliches Bild"
                width={328}
                height={328}
                sizes="100vw"
              />
            )}
            <GeoCountryAlternatives
              alternatives={getAlternatives(
                geoCountryLayer.alternatives,
                geoCountryCode?.toLowerCase(),
              )}
              closeLayer={handleClose}
            />
          </Stack>
        </Box>

        <Stack
          direction="row"
          justifyContent="right"
          alignItems="center"
          spacing={2}
          sx={{
            boxShadow:
              '0 5px 5px -3px rgba(0, 0, 0, 0.2), 0 3px 14px 2px rgba(0, 0, 0, 0.12), 0 8px 10px 1px rgba(0, 0, 0, 0.14)',
            padding: spacing(2, 3),
          }}
        >
          <Box component="span">
            <FormattedMessage
              key="geo-country-layer-footer"
              id={messages.footer.id}
              defaultMessage={messages.footer.defaultMessage}
              values={{
                spacer: ' ',
                link: (
                  <Link
                    href={`https://${fullDomain}`}
                    title={fullDomain}
                    onClick={handleClose}
                    color="inherit"
                  >
                    {fullDomain}
                  </Link>
                ),
              }}
            />
          </Box>
          <GeoCountryFlag flag={geoCountryLayer.flag} big />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
